/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { getCachedItem } from '../cache';

export const CDS_MENU_FAVORITES_KEY_PREFIX = 'cdsMenuFavorites:';

export const CDS_MENU_FAVORITES_UPDATED = 'cds-menu-favorites-updated';

/**
 * Builds the localStorage key for a menu group's favorites, scoped by entity and user.
 * Both the home page (writer) and the sidebar (reader) must use this to stay in sync.
 *
 * @param {{ entityRef?: string, userIdentityKey?: string, groupId?: string }} params
 * @returns {string|null} The storage key, or null when groupId is missing.
 */
export const getMenuFavoritesKey = ({ entityRef, userIdentityKey, groupId } = {}) => {
  if (!groupId) {
    return null;
  }

  const entity = entityRef || 'global';
  const user = userIdentityKey || 'anon';

  return `${CDS_MENU_FAVORITES_KEY_PREFIX}${entity}:${user}:${groupId}`;
};

const parseStoredFavorites = stored => {
  if (Array.isArray(stored)) {
    return stored;
  }

  if (typeof stored === 'string') {
    return JSON.parse(stored);
  }

  return null;
};

/**
 * Reads the ordered list of favorite ids from localStorage. Never prunes ids that are not
 * currently visible, so favorites hidden by permission survive and reappear when access returns.
 *
 * @param {string} key - The storage key from getMenuFavoritesKey.
 * @returns {string[]} Deduped, ordered favorite ids (empty on miss/error).
 */
export const readMenuFavorites = key => {
  if (!key) {
    return [];
  }

  try {
    const stored = getCachedItem(key);

    if (!stored) {
      return [];
    }

    const parsed = parseStoredFavorites(stored);

    if (!Array.isArray(parsed)) {
      return [];
    }

    const seen = new Set();
    const result = [];

    parsed.forEach(id => {
      if (typeof id === 'string' && id && !seen.has(id)) {
        seen.add(id);
        result.push(id);
      }
    });

    return result;
  } catch {
    return [];
  }
};

/**
 * Splits visible items into favorites (in stored order) and the rest (in input order).
 * Favorite ids without a matching visible item are skipped, so hidden favorites never render.
 *
 * @param {Array<{id: string}>} items - Already permission-filtered items.
 * @param {string[]} favoriteIds - Ordered favorite ids.
 * @returns {{ favorites: Array, others: Array }}
 */
export const orderItemsByFavorites = (items = [], favoriteIds = []) => {
  const safeItems = Array.isArray(items) ? items : [];
  const safeFavoriteIds = Array.isArray(favoriteIds) ? favoriteIds : [];

  const itemsById = new Map(safeItems.map(item => [item?.id, item]));
  const favoriteIdSet = new Set();
  const favorites = [];

  safeFavoriteIds.forEach(id => {
    const item = itemsById.get(id);

    if (item && !favoriteIdSet.has(id)) {
      favorites.push(item);
      favoriteIdSet.add(id);
    }
  });

  const others = safeItems.filter(item => !favoriteIdSet.has(item?.id));

  return { favorites, others };
};
